/**
 * Estimates how long an SMS *template* will be once its merge tags are filled
 * in, so a template surface can be measured against the same deliverability
 * ceiling as a plain message (see `sms-segments.util.ts`).
 *
 * Deliberately separate from `sms-segments.util.ts`: that file is a 1:1 port of
 * the backend's `sms_pricing.py` and must stay one, so merge-tag knowledge —
 * which is a frontend-only guess — lives here instead. The flow is
 * `estimateResolvedSmsText(template)` -> `calculateSmsSegments(estimate.text)`.
 *
 * Why an estimate is needed at all: `getTextLength(content, true)` (the length
 * every template surface used before) subtracts the whole token, so
 * `{{payment_link}}` scores 0 characters while it resolves to a ~60-character
 * URL. Authors wrote templates that passed validation and then failed at send:
 * `Message.text` is a `varchar(1600)` and `make_outbound_message` inserts
 * without `full_clean`, so an over-long render raises rather than truncates.
 */
import {
  SmsSegmentInfo,
  calculateSmsSegments,
  getSmsOverLimitMessage
} from "utils/sms-segments.util";

/**
 * Matches both brace dialects. Templates are stored single-braced
 * (`normalizeTemplateValue` rewrites `{{` -> `{` on save) and only some
 * surfaces convert back with `transformStringToTemplateValue` when loading —
 * the custom-campaign editors do not, so they hold `{tag}` in the editor while
 * appointment-type templates hold `{{tag}}`. A double-brace-only pattern would
 * silently score nothing on the campaign surfaces.
 *
 * The body is constrained to a merge-tag-shaped identifier so prose that
 * happens to contain braces is not mistaken for a tag.
 */
const MERGE_TAG_REGEXP = /\{{1,2}\s*([A-Za-z][A-Za-z0-9_.]*)\s*\}{1,2}/g;

/**
 * Estimated resolved length per merge tag, keyed on the lowercased tag name.
 *
 * Backend (snake_case) tags are the ones that matter — they are what the
 * campaign and appointment-notification templates carry, and they are resolved
 * server-side at send time by `ContextManager`. The URL lengths are derived
 * from the builders rather than guessed:
 *
 * - `reminder_confirmation_url` / `private_feedback_request_url`:
 *   `{PROTOCOL}://{DOMAIN}/confirm/{10 digits}/` — 44 characters in production.
 * - `google_review_url`: `https://search.google.com/local/writereview?placeid=`
 *   is 51 characters before a ~27-character Place ID.
 * - `clinic_address`: `street_1, street_2, city`.
 * - `patient_address`: `street_1, street_2, city, STATE ZIP`
 *   (`Patient.get_full_address`).
 *
 * Values are meant to be realistic-but-generous, not paranoid maximums: this
 * table gates saving, so a wildly inflated number blocks legitimate templates.
 */
export const MERGE_TAG_ESTIMATED_LENGTHS: Record<string, number> = {
  // Patient
  patient_first_name: 12,
  patient_preferred_name: 12,
  patient_full_name: 25,
  patient_address: 60,
  // Practice / clinic
  practice_name: 30,
  clinic_name: 30,
  clinic_display_name: 30,
  clinic_phone: 14,
  clinic_email: 35,
  clinic_address: 60,
  // Free text with no length bound on the model — the single largest
  // contributor, and the one most likely to need revising against real data.
  clinic_instructions: 200,
  // Provider
  doctor_title: 4,
  doctor_first_name: 12,
  doctor_last_name: 15,
  doctor_full_name: 30,
  // Appointment
  date: 16,
  time: 8,
  prev_date: 16,
  prev_time: 8,
  // Money / identifiers
  balance: 10,
  original_balance: 10,
  payment_amount: 10,
  days_in_collection: 3,
  service_date: 10,
  invoice_number: 10,
  side: 5,
  serial_number_or_space: 15,
  // Links
  payment_link: 60,
  payment_request_url: 60,
  patient_portal_link: 80,
  reminder_confirmation_url: 45,
  private_feedback_request_url: 45,
  google_review_url: 90,
  google_maps_url: 90,
  telehealth_join_url: 60,
  telehealth_test_url: 60,
  // Event title, start and end, clinic address and description all travel in the query string.
  google_calendar_url: 250,
  outlook_calendar_url: 250
};

/** Anything that looks like a link. */
const LINK_TAG_REGEXP = /url|link/;
/** Anything that reads as free text rather than a field. */
const LONG_TEXT_TAG_REGEXP = /instruction|note|address|description|summary/;

export const LINK_TAG_ESTIMATED_LENGTH = 60;
export const LONG_TEXT_TAG_ESTIMATED_LENGTH = 150;
export const DEFAULT_TAG_ESTIMATED_LENGTH = 25;

/**
 * Estimated resolved length for a single merge tag.
 *
 * Falls back to a shape heuristic so an unrecognised tag is still scored
 * sensibly — that covers the dotted (`{{Patient.FirstName}}`) and leads
 * (`{{lead_first_name}}`) dialects without enumerating them, and means a tag
 * added later does not silently score zero.
 */
export const getMergeTagEstimatedLength = (tagName: string): number => {
  const key = tagName.toLowerCase();

  const known = MERGE_TAG_ESTIMATED_LENGTHS[key];
  if (known !== undefined) return known;

  if (LINK_TAG_REGEXP.test(key)) return LINK_TAG_ESTIMATED_LENGTH;
  if (LONG_TEXT_TAG_REGEXP.test(key)) return LONG_TEXT_TAG_ESTIMATED_LENGTH;

  return DEFAULT_TAG_ESTIMATED_LENGTH;
};

export interface SmsTemplateEstimate {
  /** The template with every merge tag swapped for filler of its estimated length. */
  text: string;
  /** Number of merge tags found. 0 means `text` is the literal template. */
  tagCount: number;
  /** Characters the tags are estimated to contribute once resolved. */
  estimatedTagChars: number;
  /** Characters the tag tokens themselves occupy in the template. */
  literalTagChars: number;
}

/**
 * Filler is a plain GSM-7 letter so it can never flip the encoding of the
 * estimate — a template's own characters decide GSM vs UCS-2, not our guess at
 * what a clinic name might contain.
 */
const FILLER_CHAR = "x";

export interface SmsTemplateSegmentInfo extends SmsSegmentInfo {
  /** Null when the text was measured literally rather than estimated. */
  estimate: SmsTemplateEstimate | null;
}

export const estimateResolvedSmsText = (
  template: string
): SmsTemplateEstimate => {
  let tagCount = 0;
  let estimatedTagChars = 0;
  let literalTagChars = 0;

  const text = template.replace(MERGE_TAG_REGEXP, (token, tagName: string) => {
    const estimated = getMergeTagEstimatedLength(tagName);

    tagCount += 1;
    estimatedTagChars += estimated;
    literalTagChars += token.length;

    return FILLER_CHAR.repeat(estimated);
  });

  return { text, tagCount, estimatedTagChars, literalTagChars };
};

/**
 * Single entry point for SMS length feedback. `estimateMergeTags` should be true
 * exactly when the surface stores literal merge tags that a server resolves
 * later; when the surface inserts resolved values inline the text is already
 * what will be sent and is measured as-is.
 */
export const calculateTemplateSmsSegments = (
  text: string,
  estimateMergeTags: boolean
): SmsTemplateSegmentInfo => {
  if (!estimateMergeTags) {
    return { ...calculateSmsSegments(text), estimate: null };
  }

  const estimate = estimateResolvedSmsText(text);
  return { ...calculateSmsSegments(estimate.text), estimate };
};

/**
 * Whether a field's text should be scored at its projected RESOLVED length.
 *
 * The question is not "does this field show merge tags" but "will a server run
 * a template renderer over what is stored". A field that cannot insert tag
 * values necessarily stores literal tokens, so that is a sound default — but it
 * is only a proxy, and two surfaces break it: the touchpoint fields insert
 * resolved values inline (so they look like plain messages) yet are still put
 * through `format_template` at send time. Those pass `explicit`.
 */
export const shouldEstimateSmsMergeTags = ({
  explicit,
  allowMergeTags,
  allowInsertMergeTagsValues
}: {
  explicit?: boolean;
  allowMergeTags?: boolean;
  allowInsertMergeTagsValues?: boolean;
}): boolean =>
  explicit ?? Boolean(allowMergeTags && !allowInsertMergeTagsValues);

const formatNumber = (value: number) => value.toLocaleString("en-US");

/**
 * Over-limit copy. Only claims an estimate when tags are actually present —
 * a tag-free template is a certainty and should read like one.
 */
export const getSmsTemplateOverLimitMessage = (
  info: SmsTemplateSegmentInfo
): string => {
  const { estimate } = info;

  if (!estimate?.tagCount) {
    return getSmsOverLimitMessage(info);
  }

  return `This message is about ${formatNumber(
    info.charCount
  )} characters once merge tags are filled in — roughly ${formatNumber(
    estimate.estimatedTagChars
  )} of that for ${estimate.tagCount} merge tag${
    estimate.tagCount === 1 ? "" : "s"
  }. The limit is ${formatNumber(info.maxChars)}. Shorten it to save.`;
};
